import { useMemo } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://glyelurvdvrbqeyklseh.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdseWVsdXJ2ZHZyYnFleWtsc2VoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk0NDQzNzEsImV4cCI6MjA4NTAyMDM3MX0._fB90EOfQdF-E-96BLpldcAYlrsU-7P9Tvl-B6bDV4s";

let cachedClient: SupabaseClient<Database> | null = null;
let cachedToken: string | null = null;

/**
 * Custom fetch that retries once on 503 (PostgREST connection pool saturation).
 */
const retryOn503Fetch: typeof fetch = async (input, init) => {
  let response = await fetch(input, init);
  if (response.status === 503) {
    await new Promise(r => setTimeout(r, 1000));
    response = await fetch(input, init);
  }
  return response;
};

/**
 * Creates a Supabase client with session token header for RLS-protected queries.
 * Caches the client instance and only recreates when the token changes.
 */
export const createAuthenticatedClient = (sessionToken: string | null): SupabaseClient<Database> => {
  if (!sessionToken) {
    return supabase;
  }

  if (cachedClient && cachedToken === sessionToken) {
    return cachedClient;
  }

  cachedToken = sessionToken;
  cachedClient = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      storage: localStorage,
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      headers: {
        'x-session-token': sessionToken,
      },
      fetch: retryOn503Fetch,
    },
  });

  return cachedClient;
};

export const clearClientCache = () => {
  cachedClient = null;
  cachedToken = null;
};

/**
 * Hook to get an authenticated Supabase client.
 * Automatically includes the session token for RLS-protected queries.
 */
export const useSupabaseClient = (sessionToken: string | null): SupabaseClient<Database> => {
  return useMemo(() => {
    return createAuthenticatedClient(sessionToken);
  }, [sessionToken]);
};

export default useSupabaseClient;
